import { FC, useState, useMemo, useEffect } from 'react';
import { Link, Action, createAction, linkFields } from '@/types/core/Link';
import Stack from '@/shared/components/Stack/Stack';
import Button from '@/shared/components/Button/Button';
import Text from '@/shared/components/Text/Text';
import DataForm from '@/shared/components/DataForm/DataForm';
import { World } from '@/types/core/World';
import ButtonGroup from '@/shared/components/ButtonGroup/ButtonGroup';
import { useAppContext } from '@/context/AppContext';
import { Item } from '@/types/core/Item';
import { Field } from '@/types/core/Field';
import { 
  actionBaseFields, 
  minMaxFields, 
  toggleFields
} from '@/types/items/link/Action';
import DataView from '@/shared/components/DataView/DataView';

interface LinkEditorProps {
  link: Link;
  world: World;
  onBack: () => void;
  onChange: (changes: Partial<Link>) => void;
  selectItem: (id: string | null) => void;
  selectedActionIndex: number | null;
}

// Add a new prop to pass the selected action index to the parent component
interface ExtendedLinkEditorProps extends LinkEditorProps {
  setSelectedActionIndex?: (index: number | null) => void;
}

const LinkEditor: FC<ExtendedLinkEditorProps> = ({ 
  link, 
  world, 
  onBack, 
  onChange, 
  selectItem, 
  selectedActionIndex,
  setSelectedActionIndex
}) => {
  const { clock } = useAppContext();

  // Check if the selected action exists
  useEffect(() => {
    // If selectedActionIndex is not null but the action doesn't exist anymore,
    // go back to the link settings view
    if (selectedActionIndex !== null && 
        (link.actions.length === 0 || selectedActionIndex >= link.actions.length)) {
      onBack();
    }
  }, [link.actions, selectedActionIndex, onBack]);

  // Get link-specific fields based on the link type
  const getLinkSpecificFields = () => {
    switch (link.linkType) {
      case 'beat':
        return linkFields.beat;
      case 'video':
        return linkFields.video;
      case 'audio':
        return linkFields.audio;
      default:
        return [];
    }
  };

  const handleAddAction = () => {
    const newAction = createAction('minmax', `Action ${link.actions.length + 1}`);

    // Create a complete new link object with the new action
    // but preserve other properties and references
    const updatedLink = {
      ...link,
      actions: [...(link.actions || []), newAction],
      // Explicitly preserve the threeRef to prevent duplication
      threeRef: link.threeRef
    };
    
    onChange(updatedLink);
  };

  const handleUpdateAction = (index: number, action: Partial<Action>) => {
    const updatedActions = [...link.actions];
    const currentAction = updatedActions[index];
    
    // Create a new action based on the type
    let updatedAction: Action;
    if (action.type === 'toggle') {
      updatedAction = {
        ...currentAction,
        ...action,
        type: 'toggle',
        defaultState: (action as any).defaultState ?? false
      };
    } else {
      updatedAction = {
        ...currentAction,
        ...action,
        type: 'minmax',
        range: {
          min: (action as any).range?.min ?? 0,
          max: (action as any).range?.max ?? 1
        }
      };
    }
    
    updatedActions[index] = updatedAction;
    
    // Create a complete new link object with the updated actions
    // but preserve other properties and references
    onChange({
      ...link,
      actions: updatedActions,
      // Explicitly preserve the threeRef to prevent duplication
      threeRef: link.threeRef
    });
  };

  const handleDeleteAction = (index: number) => {
    const updatedActions = link.actions.filter((_, i) => i !== index);
    
    // Create a complete new link object with the updated actions
    // but preserve other properties and references
    onChange({
      ...link,
      actions: updatedActions,
      // Explicitly preserve the threeRef to prevent duplication
      threeRef: link.threeRef
    });
  };

  const getBeatOptions = () => {
    if (!clock) return [{ label: '1', value: '1' }];
    const [numerator] = clock.measure.split('/').map(Number);
    return Array.from({ length: numerator }, (_, i) => ({
      label: (i + 1).toString(),
      value: (i + 1).toString()
    }));
  };

  // Update beat link fields with dynamic options
  const updateBeatLinkFields = () => {
    if (link.linkType === 'beat') {
      const beatOptions = getBeatOptions();
      const updatedBeatFields = linkFields.beat.map(field => {
        if (field.key === 'settings.triggerBeats') {
          return {
            ...field,
            options: beatOptions
          };
        }
        return field;
      });
      return updatedBeatFields;
    }
    return getLinkSpecificFields();
  };

  const getTargetPropertyOptions = (targetItem: Item | null) => {
    if (!targetItem) return [];
    
    const properties: { label: string; value: string }[] = [];

    const addProperty = (path: string[], value: unknown) => {
      // Handle basic types
      if (typeof value === 'number' || typeof value === 'boolean') {
        properties.push({
          label: path.join('.'),
          value: path.join('.')
        });
        return;
      }

      // Handle Vector3-like objects (position, rotation, scale, etc.)
      if (typeof value === 'object' && value !== null) {
        if ('x' in value && 'y' in value && 'z' in value) {
          properties.push(
            {
              label: `${path.join('.')}.x`,
              value: `${path.join('.')}.x`
            },
            {
              label: `${path.join('.')}.y`,
              value: `${path.join('.')}.y`
            },
            {
              label: `${path.join('.')}.z`,
              value: `${path.join('.')}.z`
            }
          );
          return;
        }

        // Handle other nested objects
        if (typeof value === 'object' && value !== null) {
          Object.entries(value).forEach(([key, val]) => {
            addProperty([...path, key], val);
          });
        }
      }
    };

    // Start the recursive property traversal
    Object.entries(targetItem.properties as Record<string, unknown>).forEach(([key, value]) => {
      addProperty([key], value);
    });

    // Sort properties alphabetically
    return properties.sort((a, b) => a.label.localeCompare(b.label));
  };

  const renderLinkSettings = () => {
    const specificFields = updateBeatLinkFields();
    
    return (
      <Stack direction="vertical" gap="md">
        <DataForm
          fields={[...linkFields.common, ...specificFields]}
          values={link}
          onChange={onChange}
          world={world}
          selectItem={selectItem}
        />
      </Stack>
    );
  };

  const renderActionEditor = (actionIndex: number) => {
    const action = link.actions[actionIndex];
    const targetItem = action.targetItemId ? world.items[action.targetItemId] : null;

    // Dynamically populate target item options
    const targetItemOptions = Object.entries(world.items)
      .filter(([_, item]) => item.type !== 'link')
      .map(([id, item]) => ({
        label: item.name,
        value: id
      }));

    const actionFieldsWithOptions = actionBaseFields.map(field => {
      if (field.key === 'targetItemId') {
        return { ...field, options: targetItemOptions };
      }
      if (field.key === 'targetProperty') {
        return { ...field, options: getTargetPropertyOptions(targetItem) };
      }
      return field;
    });

    // Get additional fields based on action type
    const additionalFields = action.type === 'minmax' ? minMaxFields : toggleFields;

    return (
      <Stack direction="vertical" gap="md">
        <Stack direction="horizontal" gap="md" align="center">
          <Button onClick={onBack} icon="ArrowLeft" label="Back" />
          <Text variant="h3">{action.name}</Text>
        </Stack>
        <DataForm
          fields={actionFieldsWithOptions}
          values={action}
          onChange={(changes) => handleUpdateAction(actionIndex, changes)}
          world={world}
          selectItem={selectItem}
        />
        <DataForm
          fields={additionalFields}
          values={action}
          onChange={(changes) => handleUpdateAction(actionIndex, changes)}
          world={world}
          selectItem={selectItem}
        />
        <Button 
          variant="secondary" 
          onClick={() => {
            handleDeleteAction(actionIndex);
            onBack();
          }}
        >
          Delete Action
        </Button>
      </Stack>
    );
  };

  // Prepare actions data for DataView
  const getActionsForDataView = () => {
    return link.actions.map((action, index) => ({
      id: `${link.id}-action-${index}`,
      name: action.name,
      actionType: action.type === 'minmax' ? 'Min/Max' : 'Toggle',
      icon: 'Sliders',
      type: 'linkAction',
      linkId: link.id,
      actionIndex: index,
      action: action
    }));
  };

  // Render either the link settings or a specific action editor
  const renderContent = () => {
    if (selectedActionIndex !== null && selectedActionIndex >= 0 && selectedActionIndex < link.actions.length) {
      return renderActionEditor(selectedActionIndex);
    } else {
      return (
        <>
          <Stack direction="horizontal" gap="md" align="center" className="link-editor__header">
            <Button onClick={onBack} icon="ArrowLeft" label="Back" />
            <Text variant="h2">{link.name}</Text>
          </Stack>
          {renderLinkSettings()}
          
          <Stack direction="vertical" gap="md">
            <Stack direction="horizontal" gap="md" justify="between" align="center">
              <Text variant="h3">Actions</Text>
              <Button onClick={handleAddAction}>Add Action</Button>
            </Stack>
            <DataView
              data={getActionsForDataView()}
              onSelect={(item) => {
                if (item && item.type === 'linkAction' && typeof item.actionIndex === 'number') {
                  // Update the selected action index in the parent component
                  if (setSelectedActionIndex) {
                    setSelectedActionIndex(item.actionIndex);
                  }
                }
              }}
              selectedId={selectedActionIndex !== null ? `${link.id}-action-${selectedActionIndex}` : undefined}
            />
          </Stack>
        </>
      );
    }
  };

  return (
    <Stack direction="vertical" gap="md" className="link-editor">
      {renderContent()}
    </Stack>
  );
};

export default LinkEditor; 